import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import api from '@/api';
import { useAuth } from '@/auth/AuthContext';
import { notifications } from '@/utils/notifications';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { notifyAccountLogout, notifyAccountSwitch } from '@/utils/notifyAccountSwitch';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
  /** When true, server reassigns the user to another workspace after delete. */
  wasActiveAccount?: boolean;
  memberCount?: number;
  isOnlyWorkspace?: boolean;
  onSuccess: (accountId: string) => void;
}

const CONFIRM_PHRASE = 'DELETE';

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  accountId,
  accountName,
  wasActiveAccount = false,
  memberCount = 1,
  isOnlyWorkspace = false,
  onSuccess,
}) => {
  const { t } = useTranslation('settings');
  const { t: tToast } = useTranslation('notifications');
  const { logout } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setError(null);
    }
  }, [isOpen]);

  const confirmReady = confirmText.trim().toUpperCase() === CONFIRM_PHRASE;

  const handleDelete = async () => {
    if (!confirmReady) return;
    try {
      setIsDeleting(true);
      setError(null);
      const response = await api.delete('/users/accounts', { data: { id: accountId } });
      if (response.data.status === 'success') {
        notifications.success(tToast('deleteAccount.accountDeleted', { name: accountName }));
        if (wasActiveAccount) {
          try {
            const pref = await api.get<{ aid?: string }>('/preference');
            if (pref.data?.aid) {
              await notifyAccountSwitch(pref.data.aid);
            } else {
              await notifyAccountLogout();
              await logout();
            }
          } catch {
            await notifyAccountLogout();
            await logout();
          }
        }
        onSuccess(accountId);
        onClose();
      } else {
        throw new Error(response.data.message || t('workspace.deleteImmediate.deleteFailed'));
      }
    } catch (err: unknown) {
      console.error('Failed to delete account:', err);
      let message = t('workspace.deleteImmediate.deleteFailedRetry');
      if (isAxiosError(err)) {
        const data = err.response?.data as { message?: string; code?: string } | undefined;
        if (data?.code === 'DELETION_PENDING') {
          message = t('workspace.deleteImmediate.pendingBlocked');
        } else if (data?.message) {
          message = data.message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('workspace.deleteImmediate.title')}
      confirmLabel={t('workspace.deleteImmediate.confirmLabel')}
      confirmingLabel={t('workspace.deleteImmediate.confirming')}
      isConfirming={isDeleting}
      onConfirm={handleDelete}
      error={error}
      size="lg"
    >
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('workspace.deleteImmediate.confirmText')}{' '}
          <span className="text-foreground font-bold italic">&quot;{accountName}&quot;</span>?
        </p>
        {wasActiveAccount && (
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
            {t('workspace.deleteImmediate.activeWarning')}
          </p>
        )}
        {memberCount > 1 && (
          <p className="text-xs font-medium text-red-500/90">
            {t('workspace.deleteImmediate.teamWarning', { count: memberCount })}
          </p>
        )}
        <p className="text-muted-foreground/60 text-xs font-medium italic">
          {t('workspace.deleteImmediate.warning')}
        </p>
        {isOnlyWorkspace && (
          <p className="text-xs text-muted-foreground">
            {t('workspace.deleteImmediate.profileHint')}{' '}
            <Link to="/settings/profile" className="font-semibold text-primary hover:underline">
              {t('workspace.deleteImmediate.profileLink')}
            </Link>
          </p>
        )}
        <div className="space-y-1.5 pt-1">
          <label htmlFor="delete-workspace-confirm" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t('workspace.deleteImmediate.typeConfirm', { phrase: CONFIRM_PHRASE })}
          </label>
          <input
            id="delete-workspace-confirm"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
            className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/10"
            placeholder={CONFIRM_PHRASE}
          />
        </div>
      </div>
    </ConfirmModal>
  );
};
